import ControlManagementButton from 'components/atoms/control_management_button';
import Loader from 'components/atoms/loader';
import ManagementFinishBidding from 'components/atoms/management_finish_bidding';
import ManagementRemoveBidding from 'components/atoms/management_remove_bidding';
import BiddingItemNew from 'components/molecules/bidding_item_new';
import BiddingTimeline from 'components/molecules/bidding_timeline';
import BiddingDetailTools from 'components/organisms/bidding_detail_tools';
import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import tabId from 'services/tabId';
import { ApplicationState } from 'store';
import { getBidding } from 'store/modules/bidding_store/actions';
import { BiddingState, BiddingData } from 'store/modules/bidding_store/types';
import { getBiddingOverview, getBiddingSummary } from 'store/modules/management_store/actions';
import {
  FeatureType,
  ManagementState,
  ManagementTypes,
} from 'store/modules/management_store/types';
import { ProfileState, ProfileTypes } from 'store/modules/profile_store/types';
import BiddingOverview from 'components/organisms/bidding_overview';
import { Container } from './styles';

interface BiddingDetailsParams {
  id?: string;
  currentCompany?: string;
}

const BiddingDetails = () => {
  const { id = '', currentCompany = '' } = useParams<BiddingDetailsParams>();

  const dispatch = useDispatch();
  const [selectedFeature, setSelectedFeature] = useState<FeatureType>('tasks');

  const { biddings, request } = useSelector<ApplicationState, BiddingState>(
    (state) => state.session.biddings,
  );

  const { management } = useSelector<ApplicationState, ManagementState>(
    (state) => state.session.management,
  );

  const { profile } = useSelector<ApplicationState, ProfileState>((state) => state.storage.profile);
  const [bidding, setBidding] = useState<BiddingData | null>(null);
  const [modalFinishBiddingOpened, setModalFinishBiddingOpened] = useState<boolean>(false);
  const [modalRemoveBiddingOpened, setModalRemoveBiddingOpened] = useState<boolean>(false);
  const [overviewOpen, setOverviewOpen] = useState<boolean>(false);

  const onNavigate = (feature: FeatureType) => {
    setSelectedFeature(feature);
    setOverviewOpen(false);

    requestAnimationFrame(() => {
      document.getElementById('bidding-tabs')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    });
  };

  useEffect(() => {
    const currentBidding = biddings?.bidding_list?.find((bidding) => bidding.id.toString() === id);
    if (currentBidding) setBidding(currentBidding);
    dispatch(getBidding(id, Number(currentCompany)));
    dispatch({ type: ManagementTypes.UPDATE_STORE_FIELDS });
  }, []);

  useEffect(() => {
    dispatch({
      type: ProfileTypes.UPDATE_CURRENT_COMPANY,
      companyId: Number(currentCompany),
      tabId: tabId(),
    });
  }, []);

  useEffect(() => {
    if (id) dispatch(getBiddingSummary(Number(id), Number(currentCompany)));
    document.title = `Detalhes da licitação nº ${id} - ConLicitação`;
  }, [id]);

  useEffect(() => {
    if (!overviewOpen) return;

    const now = Date.now();
    const lastUpdate = management.overview_last_updated || 0;

    const FIVE_MINUTES = 5 * 60 * 1000;

    if (bidding?.bidding_id && (!management.overview || now - lastUpdate > FIVE_MINUTES)) {
      dispatch(getBiddingOverview(bidding.bidding_id));
    }
  }, [overviewOpen]);

  useEffect(() => {
    if (request.get_bidding.loading) return;

    const currentBidding = biddings?.bidding_list?.find((bidding) => bidding.id.toString() === id);
    if (currentBidding) setBidding(currentBidding);
  }, [
    request.get_bidding,
    biddings?.bidding_list?.find((bidding) => bidding.id.toString() === id),
  ]);

  useEffect(() => {
    if (bidding === null) return;
    if (Number(profile.currentCompany[tabId()]) === Number(currentCompany)) return;

    window.location.href = '/minhas_licitacoes/management';
  }, [profile.currentCompany[tabId()]]);

  const totalManagedTasks = (biddingId: string) => {
    return management.bidding_summary?.[biddingId]?.total_managed_opened_tasks ?? 0;
  };
  return (
    <Container>
      <div className="page-container">
        <div className="container-fluid big-resolutions">
          {request.get_bidding.loading && (
            <div className="d-flex align-items-center justify-content-center loading-container">
              <Loader />
            </div>
          )}
          {!request.get_bidding.loading && (
            <div className="row mb-4 px-md-4 py-4">
              <div className="col-12">
                <div className="row mb-2">
                  <div className="col-12">
                    <Link className="btn btn-link btn-sm p-0" to="/minhas_licitacoes/management">
                      <i className="material-icons align-middle">keyboard_arrow_left</i>
                      Voltar
                    </Link>
                  </div>
                </div>
                <div className="row mb-4">
                  <div className="col-12 col-md-6">
                    <h4 className="mb-1">Detalhes da licitação nº {id}</h4>
                    {bidding?.managers && bidding.managers.length > 0 && (
                      <p className="text-muted mb-0">
                        Gerenciada por: {bidding.managers.map((m) => m.name).join(', ')}
                      </p>
                    )}
                  </div>
                  <div className="col-12 col-md-6 text-md-right text-left">
                    <ControlManagementButton
                      id={bidding?.id || 0}
                      setModalFinishBiddingOpened={setModalFinishBiddingOpened}
                      setModalRemoveBiddingOpened={setModalRemoveBiddingOpened}
                      setOverviewOpen={setOverviewOpen}
                      managedByMe={!!bidding?.managers?.some((m) => m.user_id === profile.id)}
                    />
                  </div>
                </div>
                {bidding && (
                  <div className="mx-3">
                    <BiddingItemNew
                      bidding={bidding}
                      biddingIndex={0}
                      source="biddingDetails"
                      isChecked={false}
                      toggleSelected={() => {}}
                    />
                  </div>
                )}
                {bidding && (
                  <div id="bidding-tabs">
                    <BiddingDetailTools
                      bidding={bidding}
                      selectedFeature={selectedFeature}
                      setSelectedFeature={setSelectedFeature}
                    />
                  </div>
                )}
                {bidding && <BiddingTimeline bidding={bidding} />}
              </div>
            </div>
          )}
        </div>
        {id && (
          <>
            <ManagementFinishBidding
              modalFinishBiddingOpened={modalFinishBiddingOpened}
              setModalFinishBiddingOpened={setModalFinishBiddingOpened}
              biddingId={Number(id)}
              totalOpenedTaks={totalManagedTasks(id)}
            />
            <ManagementRemoveBidding
              modalRemoveBiddingOpened={modalRemoveBiddingOpened}
              setModalRemoveBiddingOpened={setModalRemoveBiddingOpened}
              biddingId={Number(id)}
              totalOpenedTaks={totalManagedTasks(id)}
            />
          </>
        )}
        {overviewOpen && (
          <BiddingOverview onClose={() => setOverviewOpen(false)} onNavigate={onNavigate} />
        )}
      </div>
    </Container>
  );
};

export default BiddingDetails;
